import { useEffect, useState } from 'react';
import CustomSelect from 'src/components/UI/Forms/Inputs/customSelect';
import { Input } from 'src/components/UI/Forms/Inputs';
import { useGetCompanyByTypeQuery } from 'src/services/organisation/organisation';
import DateRangeDropdown from 'src/components/UI/Forms/Inputs/dateRange';
import { useGetOrderReportsMutation } from 'src/services/orders/orders';
import 'react-tooltip/dist/react-tooltip.css';
import moment from 'moment';
// import "moment/locale/ar";
import { downloadReportCSV } from 'src/services/reports/downloadCsv';
import './reports.css';
import Spinner from 'src/components/UI/Loader/spinner';
import { getUser } from 'src/utils/user/user';
import { useTranslation } from 'react-i18next';
import { Pagination } from '@heroui/react';
import i18n from 'src/i18n';
import { FaDownload, FaMagnifyingGlass } from 'react-icons/fa6';

type OptionType = {
  code: string;
  name: string;
};

const Reports = () => {
  const [, setTransporterOptions] = useState<OptionType[]>(
    []
  );
  const [shipperOptions, setShipperOptions] = useState<OptionType[]>([]);
  const [reportData, setReportData] = useState<any[]>([]); // Data fetched from the API.
  const [filteredData, setFilteredData] = useState<any[]>([]); // Filtered data to be shown in the table.
  const [searchTerm, setSearchTerm] = useState(''); // Search term to filter the data.
  const [selectedShipper, setSelectedShipper] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedTransporter] = useState('');
  const [getReport, result] = useGetOrderReportsMutation();
  const [reportDuration, setReportDuration] = useState(
    'Showing reports for the last 24 hours'
  );
  const [selectedRange, setSelectedRange] = useState({
    from: moment().subtract(24, 'hours').valueOf(),
    to: moment().valueOf(),
  });
  const [typeTransport, setTypeTransport] = useState('');
  const [typeShipping, setTypeShipping] = useState('');
  const [transportName] = useState('');
  const [shippingName] = useState('');
  const { data: TransportCompanies } = useGetCompanyByTypeQuery({
    type: typeTransport,
    name: transportName,
  });

  const { data: ShipperCompanies } = useGetCompanyByTypeQuery({
    type: typeShipping,
    name: shippingName,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  const { t } = useTranslation();

  const user = getUser();

  const handleDateRangeChange = (range: any) => {
    const fromDate = moment(range.startDate).valueOf();
    const toDate = moment(range.endDate).valueOf();
    setSelectedRange({ from: fromDate, to: toDate });
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (TransportCompanies) {
      const options = TransportCompanies.filter(
        (company: { name: any }) => company.name
      ).map((company: any) => ({
        code: company._id,
        name: company.name,
      }));
      setTransporterOptions(options);
      setTypeTransport('transport');
    }
  }, [TransportCompanies]);

  useEffect(() => {
    if (ShipperCompanies) {
      const options = ShipperCompanies.filter(
        (company: { name: any }) => company.name
      ).map((company: any) => ({
        code: company._id,
        name: company.name,
      }));
      setShipperOptions(options);
      setTypeShipping('shipper');
    }
  }, [ShipperCompanies]);

  // When data is fetched initially or when the search term changes, filter the data.
  useEffect(() => {
    setFilteredData(
      reportData.filter((item: any) => {
        const term = searchTerm.toLowerCase();
        return (
          item.orderNumber?.toLowerCase().includes(term) ||
          item.deviceId?.toLowerCase().includes(term) ||
          item.plateNumber?.toLowerCase().includes(term) ||
          item.driver?.toLowerCase().includes(term) ||
          // item.driverIdNumber?.toLowerCase().includes(term) ||
          item.origin?.toLowerCase().includes(term) ||
          item.destination?.toLowerCase().includes(term)
        );
      })
    );
  }, [reportData, searchTerm]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const onChangeSearch = (value: any) => {
    setSearchTerm(value);
  };

  const handleDownload = async () => {
    let shipperId = '';
    if (user.type === 'shipper') {
      shipperId = user.company._id;
    }

    try {
      setIsDownloading(true);
      const csv = await downloadReportCSV(
        selectedRange.from,
        selectedRange.to,
        shipperId || selectedShipper,
        selectedTransporter
      );
      const fromDateString = moment(selectedRange.from).format('DD-MM-YYYY');
      const toDateString = moment(selectedRange.to).format('DD-MM-YYYY');
      const fileName = `${fromDateString} - ${toDateString} Report.csv`;
      downloadCSV(csv, fileName);
      setIsDownloading(false);
    } catch (error) {
      setIsDownloading(false);
    }
  };

  const handleSearch = () => {
    let shipperId = '';
    if (user.type === 'shipper') {
      shipperId = user.company._id;
    }
    setCurrentPage(1);

    const payload = {
      from: selectedRange.from,
      to: selectedRange.to,
      shipper: shipperId || selectedShipper,
      transporter: selectedTransporter,
    };

    getReport(payload)
      .unwrap()
      .then((response: any) => {
        setReportData(response);

        const fromDateString = moment(selectedRange.from).format(
          'MMMM Do YYYY'
        );
        const toDateString = moment(selectedRange.to).format('MMMM Do YYYY');

        setReportDuration(
          t('reports_page.title_text', { fromDateString, toDateString })
        );
      })
      .catch((err: any) => {
      });
  };

  const handleShipperChange = (value: any) => {
    setSelectedShipper(value);
  };



  function downloadCSV(csvContent: any, fileName: any) {
    let blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    let link = document.createElement('a');
    if (link.download !== undefined) {
      let url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  return (
    <div className="w-full h-screen overflow-y-auto" style={{ backgroundColor: "var(--bg-base)", color: "var(--text-main)" }}>
      <div className="sticky top-0 z-10 py-8 pb-4" style={{ backgroundColor: "var(--bg-base)" }}>
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="mb-2">
            <h3 className="text-2xl ">{t('reports_page.title')}</h3>
            <p className="text-sm opacity-70" style={{ color: "var(--text-muted)" }}>{reportDuration}</p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end xl:justify-end">
            <div className="w-full sm:w-[420px] lg:w-[320px]">
              <Input
                type="text"
                placeholder={t('reports_page.search_reports_textbox')}
                className="!mb-0"
                inputClassName="h-12 shadow-sm border border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)] text-[var(--text-main)]"
                onChange={(e: any) => onChangeSearch(e.target.value)}
                iconUrl="/assets/icons/search.svg"
              ></Input>
            </div>

            <div className="flex items-end">
              {isDownloading && <Spinner />}
              <button
                className="flex h-12 items-center gap-2 rounded-lg px-5 font-semibold whitespace-nowrap transition-colors duration-200"
                style={{ backgroundColor: "var(--color-primary)", color: "var(--text-invert)" }}
                onClick={(e) => {
                  handleDownload();
                }}
                disabled={
                  isDownloading || result.isLoading || reportData.length === 0
                }
              >
                {!isDownloading && <FaDownload className="text-base" />}
                {isDownloading ? (
                  <span className="loading backdrop-blur-3xl">
                    {t('reports_page.downloading_btn')}
                  </span>
                ) : (
                  <span>{t('reports_page.download_report_btn')}</span>
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-end">
          <DateRangeDropdown
            label={t('reports_page.date_range')}
            onDateRangeChange={handleDateRangeChange}
            className="!mb-0 min-w-[280px] md:w-[280px]"
          />
          {user.type !== 'shipper' && (
            <CustomSelect
              name="shipper"
              label={t('reports_page.shipping_company')}
              labelClassName="!mb-1 !text-xs !text-[var(--text-muted)]"
              options={shipperOptions as any}
              placeholder={t('reports_page.select_shipping_company')}
              className="!mb-0 min-w-[320px] md:w-[320px]"
              selectClassName="shadow-sm border border-[var(--border-light)] placeholder:text-[var(--text-muted)] text-[var(--text-main)]"
              onChange={handleShipperChange}
              errorText={t('reports_page.select_state')}
            ></CustomSelect>
          )}
          {/* <CustomSelect
              name="transporter"
              label={t("reports_page.transporter")}
              labelClassName="!text-blue/[0.7] !mb-2"
              options={transporterOptions as any}
              placeholder={t("reports_page.select_transporter")}
              className="min-w-[20%]"
              selectClassName="shadow-sm border border-[var(--border-light)] placeholder:text-[var(--text-muted)] text-[var(--text-main)]"
              onChange={handleTransporterChange}
              errorText={t("reports_page.select_state")}
            ></CustomSelect> */}

          <button
            className="flex h-12 items-center justify-center gap-2 rounded-lg px-6 font-semibold transition-colors duration-200 md:self-end"
            style={{ backgroundColor: "var(--color-primary)", color: "var(--text-invert)" }}
            onClick={handleSearch}
            disabled={result.isLoading}
          >
            <FaMagnifyingGlass className="text-sm" />
            {t('reports_page.search')}
          </button>
        </div>

        <div
          className="mt-8 overflow-hidden rounded-lg shadow-card"
          style={{ backgroundColor: "var(--bg-surface)" }}
        >
          <table
            className="w-full table-fixed border-primary"
            style={{ borderSpacing: '0' }}
          >
            <thead>
              <tr style={{ backgroundColor: "var(--bg-surface-2)", color: "var(--text-main)" }}>
                <th
                  className="rounded-tl-xl text-center text-sm"
                  style={{ padding: '15px 10px 15px 10px', width: '5%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.sl')}
                </th>
                <th
                  className="text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px 15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.order_no')}
                </th>
                <th
                  className="text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px 15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.order_type')}
                </th>
                {/* <th
                    className="text-sm text-blue/[0.5] text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis"
                    style={{ padding: "15px 10px 15px 10px", width: "10%" }}
                  >
                    {t("reports_page.shipping_company")}
                  </th> */}
                {/* <th
                    className="text-sm text-blue/[0.5] text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis"
                    style={{ padding: "15px 10px 15px 10px", width: "10%" }}
                  >
                    {t("reports_page.transporter")}
                  </th> */}
                <th
                  className="text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px 15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.device_id')}
                </th>
                <th
                  className="text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px 15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.vehicle')}
                </th>
                <th
                  className="text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px 15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.driver_details')}
                </th>
                <th
                  className="text-center p-2 w-5 whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.escort_details')}
                </th>
                <th
                  className="text-center p-2 w-5 whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.poc_details')}
                </th>
                <th
                  className="text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px 15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.origin')}
                </th>
                <th
                  className="text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px 15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.destination')}
                </th>
                <th
                  className="text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px 15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.started_on')}
                </th>
                <th
                  className="rounded-tr-xl text-center p-2 w-5 overflow-hidden whitespace-nowrap overflow-ellipsis text-sm"
                  style={{ padding: '15px 10px 15px 10px', width: '10%', color: 'var(--text-muted)' }}
                >
                  {t('reports_page.completed_on')}
                </th>
              </tr>
            </thead>
          </table>
          <div style={{ overflow: 'auto', maxHeight: '62vh' }}>
            <table
              className="w-full table-fixed border-primary"
              style={{ borderSpacing: '10px', marginTop: '-1px' }}
            >
              <tbody>
                {result.isLoading ? (
                  <tr>
                    <td colSpan={7} className="py-4 text-center loading" style={{ color: "var(--text-main)" }}>
                      Loading
                    </td>
                  </tr>
                ) : currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr
                      key={index}
                      className="cursor-default"

                    >
                      <td
                        className="text-sm text-center"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '5%',
                        }}
                      >
                        {item.slNo}
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        <span
                          data-tooltip-id="tooltip"
                          data-tooltip-content={item.orderNumber || 'N/A'}
                        >
                          {item.orderNumber || 'N/A'}
                        </span>
                        {/* <br />
                        {item.completedAt && (
                          <span className="text-xs opacity-70" style={{ color: "var(--text-muted)" }}>
                            {moment(item.completedAt).format(
                              'DD MMM YYYY HH:mm'
                            )}
                          </span>
                        )} */}
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center capitalize whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        {item.orderType || 'N/A'}
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        <span
                          data-tooltip-id="tooltip"
                          data-tooltip-content={item.deviceId}
                        >
                          {item.deviceId || 'N/A'}
                        </span>
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center capitalize whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        {item.plateNumber || 'N/A'}
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center capitalize whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        {item.driverIdNumber || 'N/A'}
                        <br />
                        <span className="text-xs opacity-70" style={{ color: "var(--text-muted)" }}>
                          {item.driver}
                        </span>
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center capitalize whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        {item.escortIdNumber || 'N/A'}
                        <br />
                        <span className="text-xs opacity-70" style={{ color: "var(--text-muted)" }}>
                          {item.escort}
                        </span>
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center capitalize whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        {item.contactPersonId || 'N/A'}
                        <br />
                        <span className="text-xs opacity-70" style={{ color: "var(--text-muted)" }}>
                          {item.contactPerson}
                        </span>
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        <span
                          data-tooltip-id="tooltip"
                          data-tooltip-content={item.origin}
                        >
                          {item.origin || 'N/A'}
                        </span>
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        <span
                          data-tooltip-id="tooltip"
                          data-tooltip-content={item.destination}
                        >
                          {item.destination || 'N/A'}
                        </span>
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center capitalize whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        <span>
                          {moment(item.startedAt).format('DD MMM YYYY HH:mm')}
                        </span>
                      </td>
                      <td
                        className="w-5 p-2 overflow-hidden text-sm text-center capitalize whitespace-nowrap overflow-ellipsis"
                        style={{
                          padding: '15px 10px 15px 10px',
                          width: '10%',
                        }}
                      >
                        <span>
                          {moment(item.completedAt).format('DD MMM YYYY HH:mm')}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="py-4 text-center" style={{ color: "var(--text-main)" }}>
                      {t('reports_page.no_reports')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {currentItems.length > 0 && !result.isLoading && (
          <div className="flex justify-center gap-5 mt-4 mr-4">
            <Pagination
              key={`pagination-${currentPage}`}
              total={totalPages}
              initialPage={currentPage}
              onChange={(page) => setCurrentPage(page)}
              page={currentPage}
              color="primary"
              size="sm"
              showShadow
              showControls={true}
              radius="full"
              classNames={{
                next: `bg-[var(--bg-overlay)] text-[var(--text-main)] ${i18n.language === 'ar' ? 'scale-x-[-1]' : 'scale-x-1'
                  }`,
                prev: `bg-[var(--bg-overlay)] text-[var(--text-main)] ${i18n.language === 'ar' ? 'scale-x-[-1]' : 'scale-x-1'
                  }`,
                item: 'bg-[var(--bg-overlay)] text-[var(--text-main)] m-1 shadow-sm',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
